/**
 * Биржевая нога одним нажатием: лента считает ордер, расширение его подписывает.
 *
 * Лента к бирже не ходит и ключа не видит. Она умеет ровно две вещи: собрать
 * ордер из чисел, которые уже показаны человеку (`poly/order.ts`), и спросить
 * расширение, готово ли оно (`poly/ask.ts`). Всё остальное — за границей окна.
 *
 * Почему это вообще одно нажатие, тогда как в конторе их будет ещё несколько.
 * У биржи нет ни страницы матча, ни формы, ни капчи: есть токен исхода, цена и
 * подпись. Значит вся дорога, которую человек проходит руками на конторе — три
 * секунды загрузки, пятнадцать поиска, пять на ввод, — здесь равна нулю. Это и
 * есть та нога, где автоматизация меняет не удобство, а число вилок в час.
 *
 * Состояний у попытки четыре, и «не знаю» среди них — полноправное. Ордер мог
 * уйти и исполниться в тот момент, когда браузер потерял связь; сказать в этом
 * месте «не куплено» значит позвать человека нажать второй раз поверх уже
 * купленной ноги, а сказать «куплено» — отправить его ставить вторую ногу
 * против позиции, которой может не быть.
 */

import { create } from 'zustand';

import type { RowLeg } from '../lib/row';
import { buy as askBuy, can as askCan, type Can, seen as askSeen } from '../poly/ask';
import { orderKey, planOrder } from '../poly/order';
import * as act from './act';
import { preflight, type Sight } from './preflight';
import type { FeedRow } from './rows';

export interface Buying {
  state: 'work' | 'done' | 'no' | 'unknown';
  say: string;
  shares?: number;
  spent?: number;
}

interface ExchangeState {
  /** Что расширение ответило про свою готовность. `null` — ещё не спрашивали. */
  can: Can | null;
  /** Попытки по вилкам. Ключ — id вилки: одна биржевая нога на строку. */
  by: Record<string, Buying>;
  askCan: (where?: Window) => Promise<void>;
  /** Вспомнить чужой памятью то, что своя потеряла при перезагрузке страницы. */
  recall: (row: FeedRow, leg: RowLeg, where?: Window) => Promise<void>;
  buy: (
    row: FeedRow,
    leg: RowLeg,
    index: 1 | 2 | 3,
    sight: Sight,
    where?: Window,
  ) => Promise<void>;
}

export const useExchange = create<ExchangeState>()((set, get) => ({
  can: null,
  by: {},

  askCan: async (where) => {
    set({ can: await askCan(where) });
  },

  recall: async (row, leg, where) => {
    const current = get().by[row.id];
    if (current?.state === 'done' || current?.state === 'no') return;
    const other = otherLeg(row, leg);
    if (other === undefined) return;
    const verdict = planOrder(leg, other);
    if (!verdict.ok) return;
    const was = await askSeen(orderKey(row.id, verdict.plan), where);
    // Ответ «не было» ничего не пишет: пустое состояние и означает «не было», а
    // запись сделала бы неотличимым «спрашивали, не было» от «уже отвечено».
    if (was.had && get().by[row.id]?.state !== 'done') say(set, row.id, { ...was, say: `${was.say} (это уже было)` });
  },

  buy: async (row, leg, index, sight, where) => {
    // Повторное нажатие по строке, где ордер уже в полёте, лента не пропускает
    // даже до расширения: там второй заслон есть, но лишний вопрос к деньгам —
    // это лишний шанс, что заслон однажды не сработает.
    const seen = get().by[row.id];
    if (seen !== undefined && seen.state !== 'no') return;

    const other = otherLeg(row, leg);
    if (other === undefined) {
      say(set, row.id, { state: 'no', say: 'второй ноги нет, потолок считать не от чего' });
      return;
    }
    // Предполётная сверка идёт до сборки ордера: сначала «можно ли вообще
    // тратить на эту строку», и только потом «сколько и по какой цене».
    const fly = preflight(row, other, sight);
    if (!fly.ok) {
      say(set, row.id, { state: 'no', say: fly.why });
      return;
    }
    const verdict = planOrder(leg, other);
    if (!verdict.ok) {
      say(set, row.id, { state: 'no', say: verdict.why });
      return;
    }

    say(set, row.id, { state: 'work', say: 'подписываем и отправляем' });
    // Отметка «пошёл ставить» ставится здесь же: замер входа считает секунды от
    // первого действия по строке, и биржевая нога, поставленная машиной за
    // полсекунды, обязана попасть в тот же счёт, что и ручная.
    act.going(row, index);
    const done = await askBuy(verdict.plan, orderKey(row.id, verdict.plan), where);
    say(set, row.id, {
      state: done.state,
      say: done.say,
      ...(typeof done.shares === 'number' ? { shares: done.shares } : {}),
      ...(typeof done.spent === 'number' ? { spent: done.spent } : {}),
    });
    if (done.state === 'done') act.placed(row, index);
  },
}));

/**
 * Нога напротив: та, чей кэф задаёт потолок цены.
 *
 * Ищется по площадке, а не по несовпадению объектов. Тождество здесь ненадёжно:
 * тот же самый исход приезжает в компонент через `legOrder`, в тест — копией, и
 * поиск «любая, кроме этой» однажды вернул бы саму биржевую ногу, то есть
 * посчитал бы потолок от цены, которую мы собираемся заплатить.
 */
export const otherLeg = (row: FeedRow, leg: RowLeg): RowLeg | undefined =>
  row.row.legs.find((one) => one.source !== leg.source);

const say = (
  set: (partial: (state: ExchangeState) => Partial<ExchangeState>) => void,
  id: string,
  one: Buying,
): void => set((state) => ({ by: { ...state.by, [id]: one } }));

/**
 * Подпись на кнопке. Отдельно от компонента, потому что это единственное, что
 * человек прочитает перед тратой денег, и оно обязано быть проверяемо тестом.
 *
 * Предполётная сверка читается здесь же, а не только при нажатии: причина
 * отказа, показанная ДО нажатия, — это объяснение, показанная ПОСЛЕ — обманутое
 * ожидание. Проверяется она всё равно дважды, потому что между чтением и
 * нажатием проходят секунды, а строка живёт десять.
 */
export function buyText(row: FeedRow, leg: RowLeg, sight: Sight): { can: boolean; text: string } {
  const other = otherLeg(row, leg);
  if (other === undefined) return { can: false, text: 'второй ноги нет, потолок считать не от чего' };
  const fly = preflight(row, other, sight);
  if (!fly.ok) return { can: false, text: fly.why };
  const verdict = planOrder(leg, other);
  if (!verdict.ok) return { can: false, text: verdict.why };
  const { plan } = verdict;
  return {
    can: true,
    text: `купить ${plan.shares.toFixed(1)} шер не дороже ${(plan.maxPrice * 100).toFixed(1)} ¢`,
  };
}
